import { motion } from "framer-motion";

/* PLACEHOLDER HEADSHOT — swap this URL for a high-res CEO photo when available. */
const TERRY_HEADSHOT_PLACEHOLDER =
    "https://cdn.prod.website-files.com/69d8175829b57ad97fa23750/69dd4d8d19d2832ec5aa5969_Image1.png";

const FoundersNoteSection = () => (
    <section
        data-testid="founders-note-section"
        className="relative py-24 md:py-32 bg-[var(--paper)] border-y border-[var(--border)]"
    >
        <div className="max-w-[1180px] mx-auto px-6 md:px-10">
            {/* Row 1: Portrait + intro */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                {/* Headshot */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-5"
                >
                    <div
                        data-testid="founders-note-photo"
                        data-placeholder="terry-headshot"
                        className="relative w-full max-w-[420px] mx-auto lg:mx-0 aspect-[4/5] rounded-2xl overflow-hidden card-paper p-0"
                    >
                        <img
                            src={TERRY_HEADSHOT_PLACEHOLDER}
                            alt="Terry Dohrmann, CEO & Co-founder of Lexafide"
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover object-center grayscale-[0.1]"
                        />
                    </div>
                </motion.div>

                {/* Intro copy */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="lg:col-span-7"
                >
                    <span className="eyebrow-pill" data-testid="founders-note-eyebrow">
                        The people behind it
                    </span>
                    <h2
                        className="font-display text-[clamp(1.9rem,3.8vw,2.8rem)] leading-[1.08] tracking-tight font-semibold text-[var(--ink)] mt-5"
                        data-testid="founders-note-heading"
                    >
                        Built by people who&apos;ve been <span className="accent-blue">inside the matrix</span>, not just studied it.
                    </h2>
                    <p
                        className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)]"
                        data-testid="founders-note-body"
                    >
                        Most legal AI is built by software companies that have never had to explain a capacity gap to a managing partner. Lexafide was built by people who have. Our founders spent the last two decades inside law firm operations — co-founding Litify, building at Salesforce, working hands-on with firms from solo practices to Morgan &amp; Morgan, and scaling enterprise AI companies. We know what it means when the person picking up the phone is representing a name on the building, and we know exactly where capacity and margin get lost along the way. It&apos;s why every agent ships with{" "}
                        <span className="text-[var(--ink)] font-medium">confidence thresholds you set, human override in one click, and a pilot before you commit.</span>
                    </p>
                </motion.div>
            </div>

            {/* Row 2: Pull quote — restrained editorial treatment */}
            <motion.figure
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                data-testid="founders-note-pullquote"
                className="relative mt-14 md:mt-16"
            >
                <div className="relative border-l-2 border-[var(--blue)] pl-6 md:pl-8">
                    <i
                        aria-hidden
                        className="fa-solid fa-quote-left accent-blue text-lg opacity-70 mb-3 block"
                    />
                    <blockquote className="font-display font-medium text-[1.15rem] md:text-[1.3rem] leading-[1.45] tracking-tight text-[var(--ink)]">
                        15 years of walking law firm hallways taught me where the real friction lives — not in the software gaps, but in the moments where staff are stuck doing work that pulls them away from clients. When every person on the team is freed up and equipped to do their best work, everything else follows: happier clients, stronger revenue, and a staff that actually wants to stay. That&apos;s the company we built.
                    </blockquote>

                    <figcaption className="mt-6 flex items-center gap-3">
                        <img
                            src={TERRY_HEADSHOT_PLACEHOLDER}
                            alt="Terry Dohrmann"
                            loading="lazy"
                            className="w-10 h-10 rounded-full object-cover object-center flex-shrink-0"
                        />
                        <div className="text-[13px] leading-tight">
                            <div className="text-[var(--ink)] font-semibold">Terry Dohrmann</div>
                            <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--ink-dim)] mt-1">
                                CEO &amp; Co-founder · Co-founder, Litify · ex-Salesforce
                            </div>
                        </div>
                    </figcaption>
                </div>
            </motion.figure>
        </div>
    </section>
);

export default FoundersNoteSection;
